import React, { useState, useEffect, useContext, useCallback } from "react";
import { TranslatorContext } from "../../../../context/Translator";
import axios from "axios";
import { Table, Row, Col, Form } from "react-bootstrap";
import PageLayout from "../../../../layouts/PageLayout";
import { Link } from "react-router-dom";

export default function FinanceReport() {
    const { t } = useContext(TranslatorContext);

    const API_BASE = `${process.env.REACT_APP_API_URL}/admin/finance/report`;
    const authHeaders = { headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` } };

    const [loading, setLoading] = useState(true);
    const [reportData, setReportData] = useState(null);

    const [filters, setFilters] = useState({
        from_date: "2026-08-01",
        to_date: "2026-08-31"
    });

    const fetchFinanceReport = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (filters.from_date) params.from_date = filters.from_date;
            if (filters.to_date) params.to_date = filters.to_date;

            const res = await axios.get(API_BASE, { ...authHeaders, params });
            const data = res.data?.data || null;
            setReportData(data);
        } catch (err) {
            console.error("Error fetching main finance report:", err);
        } finally {
            setLoading(false);
        }
    }, [API_BASE, filters]);

    useEffect(() => {
        const timer = setTimeout(() => {
            fetchFinanceReport();
        }, 300);
        return () => clearTimeout(timer);
    }, [fetchFinanceReport]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters((prev) => ({ ...prev, [name]: value }));
    };

    const handleResetFilters = () => {
        setFilters({
            from_date: "",
            to_date: ""
        });
    };

    const summary = reportData?.summary || {};
    const expenseBreakdown = reportData?.expense_breakdown || {};
    const products = reportData?.products || [];
    const salaryExpenses = reportData?.salary_expenses || [];
    const utilityExpenses = reportData?.utility_expenses || [];
    const dailyExpenses = reportData?.daily_expenses || [];

    const formatCurrency = (val) => {
        const num = parseFloat(val || 0);
        return num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    return (
        <PageLayout>
            <Row>
                {/* Header Breadcrumb */}
                <Col xl={12}>
                    <div className="mc-card mb-4">
                        <div className="mc-breadcrumb">
                            <h3 className="mc-breadcrumb-title">{t("Overall Finance Report")}</h3>
                            <ul className="mc-breadcrumb-list">
                                <li className="mc-breadcrumb-item">
                                    <Link to="#" className="mc-breadcrumb-link">{t("home")}</Link>
                                </li>
                                <li className="mc-breadcrumb-item">{t("finance report")}</li>
                            </ul>
                        </div>
                    </div>
                </Col>

                {/* KPI Cards */}
                <Col xl={4} md={6} className="mb-4">
                    <div className="mc-card p-4 d-flex align-items-center justify-content-between" style={{ borderLeft: "5px solid #28a745", background: "#f8f9fa" }}>
                        <div>
                            <span className="text-muted fw-bold text-uppercase d-block mb-1" style={{ fontSize: "12px" }}>
                                {t("Total Revenue")}
                            </span>
                            <h3 className="mb-0 text-success fw-bold">
                                ${formatCurrency(summary.order_revenue)}
                            </h3>
                        </div>
                        <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: "50px", height: "50px", backgroundColor: "rgba(40, 167, 69, 0.1)" }}>
                            <i className="material-icons text-success" style={{ fontSize: "28px" }}>arrow_downward</i>
                        </div>
                    </div>
                </Col>

                <Col xl={4} md={6} className="mb-4">
                    <div className="mc-card p-4 d-flex align-items-center justify-content-between" style={{ borderLeft: "5px solid #dc3545", background: "#f8f9fa" }}>
                        <div>
                            <span className="text-muted fw-bold text-uppercase d-block mb-1" style={{ fontSize: "12px" }}>
                                {t("Grand Total Expenses")}
                            </span>
                            <h3 className="mb-0 text-danger fw-bold">
                                ${formatCurrency(summary.grand_total_expense)}
                            </h3>
                        </div>
                        <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: "50px", height: "50px", backgroundColor: "rgba(220, 53, 69, 0.1)" }}>
                            <i className="material-icons text-danger" style={{ fontSize: "28px" }}>arrow_upward</i>
                        </div>
                    </div>
                </Col>

                <Col xl={4} md={12} className="mb-4">
                    <div className="mc-card p-4 d-flex align-items-center justify-content-between" style={{ borderLeft: "5px solid #4f46e5", background: "#f8f9fa" }}>
                        <div>
                            <span className="text-muted fw-bold text-uppercase d-block mb-1" style={{ fontSize: "12px" }}>
                                {t("Net Profit / Loss")}
                            </span>
                            <h3 className={`mb-0 fw-bold ${parseFloat(summary.net_profit || 0) >= 0 ? "text-primary" : "text-danger"}`}>
                                ${formatCurrency(summary.net_profit)}
                            </h3>
                        </div>
                        <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: "50px", height: "50px", backgroundColor: "rgba(79, 70, 229, 0.1)" }}>
                            <i className="material-icons style-primary" style={{ fontSize: "28px", color: "#4f46e5" }}>account_balance_wallet</i>
                        </div>
                    </div>
                </Col>

                {/* Filters */}
                <Col xl={12} className="mb-4">
                    <div className="mc-card p-3">
                        <div className="mc-card-header mb-3">
                            <h5 className="mc-card-title mb-0">{t("Filter Reports")}</h5>
                        </div>
                        <Row className="g-3">
                            <Col lg={5} md={5}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">{t("From Date")}</Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="from_date"
                                        value={filters.from_date}
                                        onChange={handleFilterChange}
                                    />
                                </Form.Group>
                            </Col>

                            <Col lg={5} md={5}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">{t("To Date")}</Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="to_date"
                                        value={filters.to_date}
                                        onChange={handleFilterChange}
                                    />
                                </Form.Group>
                            </Col>

                            <Col lg={2} md={2} className="d-flex align-items-end">
                                <button type="button" className="btn btn-secondary w-100" onClick={handleResetFilters}>
                                    {t("Reset")}
                                </button>
                            </Col>
                        </Row>
                    </div>
                </Col>

                {/* Expense Summary Cards */}
                <Col lg={6} className="mb-4">
                    <div className="mc-card p-3 h-100">
                        <div className="mc-card-header mb-3">
                            <h5 className="mc-card-title mb-0">{t("Product Expense Summary")}</h5>
                        </div>
                        <div className="d-flex flex-column gap-2" style={{ fontSize: "14px" }}>
                            <div className="d-flex justify-content-between text-muted">
                                <span>{t("Buying Expense")}:</span>
                                <span className="fw-bold text-dark">${formatCurrency(expenseBreakdown.product_expenses?.buying_expense)}</span>
                            </div>
                            <div className="d-flex justify-content-between text-muted">
                                <span>{t("Shipping Expense")}:</span>
                                <span className="fw-bold text-dark">${formatCurrency(expenseBreakdown.product_expenses?.shipping_expense)}</span>
                            </div>
                            <div className="d-flex justify-content-between text-muted">
                                <span>{t("Lister Expense")}:</span>
                                <span className="fw-bold text-dark">${formatCurrency(expenseBreakdown.product_expenses?.lister_expense)}</span>
                            </div>
                            <div className="d-flex justify-content-between text-muted">
                                <span>{t("Market Placer Expense")}:</span>
                                <span className="fw-bold text-dark">${formatCurrency(expenseBreakdown.product_expenses?.market_placer_expense)}</span>
                            </div>
                            <hr className="my-1" />
                            <div className="d-flex justify-content-between fw-bold text-dark">
                                <span>{t("Total Product Expenses")}:</span>
                                <span className="text-danger">${formatCurrency(expenseBreakdown.product_expenses?.total)}</span>
                            </div>
                        </div>
                    </div>
                </Col>

                <Col lg={6} className="mb-4">
                    <div className="mc-card p-3 h-100">
                        <div className="mc-card-header mb-3">
                            <h5 className="mc-card-title mb-0">{t("Operational Expense Summary")}</h5>
                        </div>
                        <div className="d-flex flex-column gap-2" style={{ fontSize: "14px" }}>
                            <div className="d-flex justify-content-between text-muted">
                                <span>{t("Salary Expense")}:</span>
                                <span className="fw-bold text-dark">${formatCurrency(expenseBreakdown.operational_expenses?.salary_expense)}</span>
                            </div>
                            <div className="d-flex justify-content-between text-muted">
                                <span>{t("Utility Expense")}:</span>
                                <span className="fw-bold text-dark">${formatCurrency(expenseBreakdown.operational_expenses?.utility_expense)}</span>
                            </div>
                            <div className="d-flex justify-content-between text-muted">
                                <span>{t("Daily Expense")}:</span>
                                <span className="fw-bold text-dark">${formatCurrency(expenseBreakdown.operational_expenses?.daily_expense)}</span>
                            </div>
                            <hr className="my-1" />
                            <div className="d-flex justify-content-between fw-bold text-dark">
                                <span>{t("Total Operational Expenses")}:</span>
                                <span className="text-danger">${formatCurrency(expenseBreakdown.operational_expenses?.total)}</span>
                            </div>
                        </div>
                    </div>
                </Col>

                {/* Product Performance Breakdown Table */}
                <Col xl={12} className="mb-4">
                    <div className="mc-card">
                        <div className="mc-card-header p-3">
                            <h5 className="mc-card-title mb-0">{t("Product Performance Breakdown")}</h5>
                        </div>
                        <div className="table-responsive-wrapper">
                            <Table responsive className="table align-middle mb-0" style={{ borderCollapse: "separate", borderSpacing: "0" }}>
                                <thead style={{ backgroundColor: "#4f46e5", color: "#ffffff" }}>
                                    <tr>
                                        <th className="text-white text-center" style={{ width: "60px" }}>#</th>
                                        <th className="text-white">{t("PRODUCT DETAILS")}</th>
                                        <th className="text-white">{t("EXPENSE BREAKDOWN")}</th>
                                        <th className="text-white text-center" style={{ width: "260px" }}>{t("FINANCIAL SUMMARY")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        <tr>
                                            <td colSpan="4" className="text-center p-4">{t("Loading finance data...")}</td>
                                        </tr>
                                    ) : products.length === 0 ? (
                                        <tr>
                                            <td colSpan="4" className="text-center p-4">{t("No product records found.")}</td>
                                        </tr>
                                    ) : (
                                        products.map((item, index) => (
                                            <tr key={item.id || index} style={{ borderBottom: "1px solid #e5e7eb" }}>
                                                <td className="text-center fw-bold">{index + 1}</td>
                                                <td>
                                                    <div className="d-flex align-items-center gap-3">
                                                        {item.card_img ? (
                                                            <img
                                                                src={`${process.env.REACT_APP_IMG_URL}/${item.card_img}`}
                                                                alt={item.title}
                                                                className="rounded"
                                                                style={{ width: "50px", height: "50px", objectFit: "cover" }}
                                                            />
                                                        ) : (
                                                            <div className="rounded bg-light d-flex align-items-center justify-content-center text-muted" style={{ width: "50px", height: "50px", fontSize: "10px" }}>
                                                                NO IMG
                                                            </div>
                                                        )}
                                                        <div>
                                                            <h6 className="mb-1 text-dark fw-bold" style={{ fontSize: "14px" }}>{item.title}</h6>
                                                            <div className="d-flex flex-wrap gap-2 text-muted" style={{ fontSize: "12px" }}>
                                                                <span><strong>Code:</strong> {item.product_code}</span>
                                                                <span>|</span>
                                                                <span><strong>Store:</strong> {item.store?.name || "N/A"}</span>
                                                                <span>|</span>
                                                                <span><strong>Brand:</strong> {item.brand?.name || "N/A"}</span>
                                                                <span>|</span>
                                                                <span><strong>Marketplace:</strong> {item.marketplace?.name || "N/A"}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td style={{ verticalAlign: "middle" }}>
                                                    <div style={{ fontSize: "13px", lineHeight: "1.8" }}>
                                                        {item.expense_breakdown?.map((exp, expIdx) => (
                                                            <div key={exp.key || expIdx} className="d-flex justify-content-between pe-4">
                                                                <span className="text-muted">
                                                                    {exp.name} {exp.percentage ? `(${exp.percentage}%)` : ""}:
                                                                </span>
                                                                <span className="fw-bold text-dark">${exp.amount || "0.00"}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </td>
                                                <td className="align-middle px-3" style={{ verticalAlign: "middle" }}>
                                                    <div style={{ fontSize: "13px", lineHeight: "1.8" }}>
                                                        <div className="d-flex justify-content-between mb-1">
                                                            <span className="text-muted fw-bold">Order Price:</span>
                                                            <span className="fw-bold text-dark">${item.amounts?.order_price || "0.00"}</span>
                                                        </div>
                                                        <div className="d-flex justify-content-between mb-2">
                                                            <span className="text-muted fw-bold">Total Expense:</span>
                                                            <span className="fw-bold text-danger">${item.amounts?.total_expense || "0.00"}</span>
                                                        </div>
                                                        <div
                                                            className="rounded p-2 text-center text-white fw-bold shadow-sm"
                                                            style={{
                                                                backgroundColor: parseFloat(item.amounts?.profit || 0) >= 0 ? "#4f46e5" : "#dc3545",
                                                                fontSize: "15px"
                                                            }}
                                                        >
                                                            PROFIT: ${item.amounts?.profit || "0.00"}
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </Table>
                        </div>
                    </div>
                </Col>

                {/* Salary Expenses Table */}
                <Col xl={12} className="mb-4">
                    <div className="mc-card">
                        <div className="mc-card-header p-3">
                            <h5 className="mc-card-title mb-0">{t("Salary Expenses Log")}</h5>
                        </div>
                        <div className="table-responsive-wrapper">
                            <Table responsive className="table align-middle mb-0">
                                <thead style={{ backgroundColor: "#3b82f6", color: "#ffffff" }}>
                                    <tr>
                                        <th className="text-white text-center" style={{ width: "60px" }}>#</th>
                                        <th className="text-white">{t("Employee")}</th>
                                        <th className="text-white">{t("Entered By")}</th>
                                        <th className="text-white text-center">{t("Salary Month")}</th>
                                        <th className="text-white text-center">{t("Payment Date")}</th>
                                        <th className="text-white text-center">{t("Payment Account")}</th>
                                        <th className="text-white text-end">{t("Amount")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {salaryExpenses.length === 0 ? (
                                        <tr>
                                            <td colSpan="7" className="text-center p-4">{t("No salary expenses found.")}</td>
                                        </tr>
                                    ) : (
                                        salaryExpenses.map((s, index) => (
                                            <tr key={s.id || index}>
                                                <td className="text-center fw-bold">{index + 1}</td>
                                                <td>
                                                    <div className="fw-bold text-dark">{s.employee?.name || "N/A"}</div>
                                                    <small className="text-muted">{s.employee?.email}</small>
                                                </td>
                                                <td>{s.entered_by?.name || "N/A"}</td>
                                                <td className="text-center">{s.salary_month || "N/A"}</td>
                                                <td className="text-center">{s.date || "N/A"}</td>
                                                <td className="text-center">
                                                    <span className="badge bg-light text-dark border">
                                                        {s.payment?.bank?.name || "N/A"}
                                                    </span>
                                                </td>
                                                <td className="text-end fw-bold text-danger">${formatCurrency(s.salary)}</td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </Table>
                        </div>
                    </div>
                </Col>

                {/* Utility Expenses Table */}
                <Col xl={12} className="mb-4">
                    <div className="mc-card">
                        <div className="mc-card-header p-3">
                            <h5 className="mc-card-title mb-0">{t("Utility Expenses Log")}</h5>
                        </div>
                        <div className="table-responsive-wrapper">
                            <Table responsive className="table align-middle mb-0">
                                <thead style={{ backgroundColor: "#0284c7", color: "#ffffff" }}>
                                    <tr>
                                        <th className="text-white text-center" style={{ width: "60px" }}>#</th>
                                        <th className="text-white">{t("Expense Type")}</th>
                                        <th className="text-white">{t("Entered By")}</th>
                                        <th className="text-white text-center">{t("Date")}</th>
                                        <th className="text-white">{t("Note")}</th>
                                        <th className="text-white text-center">{t("Payment Account")}</th>
                                        <th className="text-white text-end">{t("Amount")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {utilityExpenses.length === 0 ? (
                                        <tr>
                                            <td colSpan="7" className="text-center p-4">{t("No utility expenses found.")}</td>
                                        </tr>
                                    ) : (
                                        utilityExpenses.map((u, index) => (
                                            <tr key={u.id || index}>
                                                <td className="text-center fw-bold">{index + 1}</td>
                                                <td className="fw-bold text-dark">{u.expense_type?.name || "N/A"}</td>
                                                <td>{u.entered_by?.name || "N/A"}</td>
                                                <td className="text-center">{u.date || "N/A"}</td>
                                                <td className="text-muted">{u.note || "N/A"}</td>
                                                <td className="text-center">
                                                    <span className="badge bg-light text-dark border">
                                                        {u.payment?.bank?.name || "N/A"}
                                                    </span>
                                                </td>
                                                <td className="text-end fw-bold text-danger">${formatCurrency(u.amount)}</td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </Table>
                        </div>
                    </div>
                </Col>

                {/* Daily Expenses Table */}
                <Col xl={12} className="mb-4">
                    <div className="mc-card">
                        <div className="mc-card-header p-3">
                            <h5 className="mc-card-title mb-0">{t("Daily Expenses Log")}</h5>
                        </div>
                        <div className="table-responsive-wrapper">
                            <Table responsive className="table align-middle mb-0">
                                <thead style={{ backgroundColor: "#059669", color: "#ffffff" }}>
                                    <tr>
                                        <th className="text-white text-center" style={{ width: "60px" }}>#</th>
                                        <th className="text-white">{t("Details / Expense Types")}</th>
                                        <th className="text-white">{t("Entered By")}</th>
                                        <th className="text-white text-center">{t("Date")}</th>
                                        <th className="text-white">{t("Note")}</th>
                                        <th className="text-white text-center">{t("Payment Account")}</th>
                                        <th className="text-white text-end">{t("Total Amount")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {dailyExpenses.length === 0 ? (
                                        <tr>
                                            <td colSpan="7" className="text-center p-4">{t("No daily expenses found.")}</td>
                                        </tr>
                                    ) : (
                                        dailyExpenses.map((d, index) => (
                                            <tr key={d.id || index}>
                                                <td className="text-center fw-bold">{index + 1}</td>
                                                <td>
                                                    <div style={{ fontSize: "13px" }}>
                                                        {d.details?.map((det, detIdx) => (
                                                            <div key={det.id || detIdx} className="d-flex justify-content-between pe-3">
                                                                <span className="text-muted">{det.expense_type}:</span>
                                                                <span className="fw-bold">${formatCurrency(det.amount)}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </td>
                                                <td>{d.entered_by?.name || "N/A"}</td>
                                                <td className="text-center">{d.date || "N/A"}</td>
                                                <td className="text-muted">{d.note || "N/A"}</td>
                                                <td className="text-center">
                                                    <span className="badge bg-light text-dark border">
                                                        {d.payment?.bank_name || "N/A"}
                                                    </span>
                                                </td>
                                                <td className="text-end fw-bold text-danger">${formatCurrency(d.total_amount)}</td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </Table>
                        </div>
                    </div>
                </Col>
            </Row>
        </PageLayout>
    );
}